import React from "react";
import {
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Info,
  Business,
  Assessment,
} from "@mui/icons-material";

interface TradeMarketContextProps {
  trade: {
    symbol: string;
    marketContext?: {
      currentPrice?: number;
      priceChange?: number;
      priceChangePercent?: number;
      volume?: number;
      lastUpdated?: string;
      hasMarketData: boolean;
      availableIndicators?: string[];
      companyInfo?: {
        name?: string;
        sector?: string;
        industry?: string;
        marketCap?: number;
      };
      priceComparison?: {
        entryPrice?: number;
        currentPrice?: number;
        priceChange?: number;
        priceChangePercent?: number;
        pnlImpact?: {
          unrealizedPnl?: number;
          pnlPercent?: number;
          totalPositionValue?: number;
          totalEntryValue?: number;
          isProfitable?: boolean;
        };
      };
      marketConditions?: {
        volatility?: number;
        volume?: number;
        rsi?: number;
        movingAverage?: number;
        support?: number;
        resistance?: number;
        timestamp?: string;
        marketSentiment?: string;
      };
      whatIfScenarios?: Array<{
        scenario: string;
        entryPrice?: number;
        exitPrice?: number;
        entryTime?: string;
        exitTime?: string;
        pnl?: number;
        pnlPercent?: number;
        description: string;
        isOptimal?: boolean;
      }>;
    };
  };
  compact?: boolean;
}

export const TradeMarketContext: React.FC<TradeMarketContextProps> = ({
  trade,
  compact = false,
}) => {
  const { symbol, marketContext } = trade;

  if (!marketContext?.hasMarketData) {
    return (
      <Box
        sx={{
          p: compact ? 1 : 2,
          bgcolor: "grey.100",
          borderRadius: 1,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No market data available for {symbol}
        </Typography>
      </Box>
    );
  }

  const {
    currentPrice,
    priceChange,
    priceChangePercent,
    volume,
    lastUpdated,
    availableIndicators,
    companyInfo,
    priceComparison,
    marketConditions,
    whatIfScenarios,
  } = marketContext;

  const isPositive = (priceChange || 0) >= 0;
  const isNeutral = (priceChange || 0) === 0;

  const getTrendIcon = () => {
    if (isNeutral) return <TrendingFlat />;
    return isPositive ? <TrendingUp /> : <TrendingDown />;
  };

  const getTrendColor = () => {
    if (isNeutral) return "text.secondary";
    return isPositive ? "success.main" : "error.main";
  };

  const formatPrice = (price?: number) => {
    return price ? `$${price.toFixed(2)}` : "N/A";
  };

  const formatChange = (change?: number, percent?: number) => {
    if (change === undefined || percent === undefined) return "N/A";
    const sign = change >= 0 ? "+" : "";
    return `${sign}${change.toFixed(2)} (${sign}${percent.toFixed(2)}%)`;
  };

  const formatVolume = (vol?: number) => {
    if (!vol) return "N/A";
    if (vol >= 1e9) return `${(vol / 1e9).toFixed(1)}B`;
    if (vol >= 1e6) return `${(vol / 1e6).toFixed(1)}M`;
    if (vol >= 1e3) return `${(vol / 1e3).toFixed(1)}K`;
    return vol.toString();
  };

  const formatMarketCap = (cap?: number) => {
    if (!cap) return "N/A";
    if (cap >= 1e12) return `$${(cap / 1e12).toFixed(1)}T`;
    if (cap >= 1e9) return `$${(cap / 1e9).toFixed(1)}B`;
    if (cap >= 1e6) return `$${(cap / 1e6).toFixed(1)}M`;
    return `$${cap.toFixed(0)}`;
  };

  if (compact) {
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Typography variant="body2" fontWeight="bold">
          {formatPrice(currentPrice)}
        </Typography>
        <Typography
          variant="caption"
          color={getTrendColor()}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          {getTrendIcon()}
          {formatChange(priceChange, priceChangePercent)}
        </Typography>
        {priceComparison?.pnlImpact && (
          <Typography
            variant="caption"
            color={
              priceComparison.pnlImpact.isProfitable
                ? "success.main"
                : "error.main"
            }
            fontWeight="bold"
          >
            P&L: {priceComparison.pnlImpact.isProfitable ? "+" : ""}$
            {priceComparison.pnlImpact.unrealizedPnl?.toFixed(2)}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="div">
            Market Data for {symbol}
          </Typography>
          <Tooltip title="Market data provided by external service">
            <IconButton size="small">
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={2}>
          {/* Price Information */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ textAlign: "center", p: 1 }}>
              <Typography variant="h4" component="div" fontWeight="bold">
                {formatPrice(currentPrice)}
              </Typography>
              <Typography
                variant="h6"
                color={getTrendColor()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                }}
              >
                {getTrendIcon()}
                {formatChange(priceChange, priceChangePercent)}
              </Typography>
            </Box>
          </Grid>

          {/* Volume and Last Updated */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ textAlign: "center", p: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Volume
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatVolume(volume)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last updated:{" "}
                {lastUpdated ? new Date(lastUpdated).toLocaleString() : "N/A"}
              </Typography>
            </Box>
          </Grid>

          {/* Company Information */}
          {companyInfo && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Business fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Company Information
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {companyInfo.name && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Name:</strong> {companyInfo.name}
                      </Typography>
                    </Grid>
                  )}
                  {companyInfo.sector && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Sector:</strong> {companyInfo.sector}
                      </Typography>
                    </Grid>
                  )}
                  {companyInfo.industry && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Industry:</strong> {companyInfo.industry}
                      </Typography>
                    </Grid>
                  )}
                  {companyInfo.marketCap && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Market Cap:</strong>{" "}
                        {formatMarketCap(companyInfo.marketCap)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </>
          )}

          {/* Price Comparison & P&L Impact */}
          {priceComparison && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <TrendingUp fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Trade Performance
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Entry Price: ${priceComparison.entryPrice?.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Current Price: ${priceComparison.currentPrice?.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Price Change:{" "}
                      {priceComparison.priceChange &&
                      priceComparison.priceChange >= 0
                        ? "+"
                        : ""}
                      ${priceComparison.priceChange?.toFixed(2)} (
                      {priceComparison.priceChangePercent &&
                      priceComparison.priceChangePercent >= 0
                        ? "+"
                        : ""}
                      {priceComparison.priceChangePercent?.toFixed(2)}%)
                    </Typography>
                  </Grid>
                  {priceComparison.pnlImpact && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: priceComparison.pnlImpact.isProfitable
                            ? "success.50"
                            : "error.50",
                          borderRadius: 1,
                          border: `1px solid ${priceComparison.pnlImpact.isProfitable ? "success.main" : "error.main"}`,
                        }}
                      >
                        <Typography
                          variant="h6"
                          color={
                            priceComparison.pnlImpact.isProfitable
                              ? "success.main"
                              : "error.main"
                          }
                          fontWeight="bold"
                        >
                          {priceComparison.pnlImpact.isProfitable ? "+" : ""}$
                          {priceComparison.pnlImpact.unrealizedPnl?.toFixed(2)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={
                            priceComparison.pnlImpact.isProfitable
                              ? "success.main"
                              : "error.main"
                          }
                        >
                          {priceComparison.pnlImpact.isProfitable ? "+" : ""}
                          {priceComparison.pnlImpact.pnlPercent?.toFixed(2)}%
                          P&L
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          Position Value: $
                          {priceComparison.pnlImpact.totalPositionValue?.toFixed(
                            2,
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </>
          )}

          {/* Market Conditions */}
          {marketConditions && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Assessment fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Market Conditions at Trade Time
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {marketConditions.volatility && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Volatility
                      </Typography>
                      <Typography variant="body2">
                        {marketConditions.volatility.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  {marketConditions.rsi && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        RSI
                      </Typography>
                      <Typography variant="body2">
                        {marketConditions.rsi.toFixed(1)}
                      </Typography>
                    </Grid>
                  )}
                  {marketConditions.movingAverage && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        MA
                      </Typography>
                      <Typography variant="body2">
                        ${marketConditions.movingAverage.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  {marketConditions.marketSentiment && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Sentiment
                      </Typography>
                      <Chip
                        label={marketConditions.marketSentiment}
                        size="small"
                        color={
                          marketConditions.marketSentiment === "BULLISH"
                            ? "success"
                            : marketConditions.marketSentiment === "BEARISH"
                              ? "error"
                              : "default"
                        }
                      />
                    </Grid>
                  )}
                  {marketConditions.support && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Support
                      </Typography>
                      <Typography variant="body2">
                        ${marketConditions.support.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  {marketConditions.resistance && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Resistance
                      </Typography>
                      <Typography variant="body2">
                        ${marketConditions.resistance.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </>
          )}

          {/* What-if Scenarios */}
          {whatIfScenarios && whatIfScenarios.length > 0 && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <TrendingDown fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    What-if Analysis
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {whatIfScenarios.map((scenario, index) => (
                    <Grid item xs={12} key={index}>
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: scenario.isOptimal
                            ? "primary.50"
                            : "grey.50",
                          borderRadius: 1,
                          border: `1px solid ${scenario.isOptimal ? "primary.main" : "grey.300"}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            {scenario.scenario}
                          </Typography>
                          <Chip
                            label={scenario.isOptimal ? "OPTIMAL" : "SCENARIO"}
                            size="small"
                            color={scenario.isOptimal ? "primary" : "default"}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {scenario.description}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                          <Typography variant="caption">
                            Entry: ${scenario.entryPrice?.toFixed(2)}
                          </Typography>
                          <Typography variant="caption">
                            P&L: {scenario.pnl && scenario.pnl >= 0 ? "+" : ""}$
                            {scenario.pnl?.toFixed(2)} (
                            {scenario.pnlPercent && scenario.pnlPercent >= 0
                              ? "+"
                              : ""}
                            {scenario.pnlPercent?.toFixed(2)}%)
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </>
          )}

          {/* Available Indicators */}
          {availableIndicators && availableIndicators.length > 0 && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Assessment fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Available Indicators
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {availableIndicators.map((indicator, index) => (
                    <Chip
                      key={index}
                      label={indicator}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TradeMarketContext;
